import React, {useState, useEffect} from 'react';
import {getMassUploadFiles, deleteMassUploadFile, downloadMassUploadSourceFile} from '../../services/massUploadApi';
import {toast} from 'react-toastify';
import MassUploadModal from '../../modals/massUploadModal';
import GridHeader from '../../components/GridHeader';
import MassUploadFilters from './components/MassUploadFilters';
import PaginationControl from './components/PaginationControl';
import MassUploadTable from './components/MassUploadTable';
import {Button} from 'semantic-ui-react';
import SvgNew from '../../components/SvgNew';

const MassUploadListPage = ({history}) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [authorFilter, setAuthorFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [previousStatuses, setPreviousStatuses] = useState({});

    useEffect(() => {
        loadFiles();
    }, []);

    useEffect(() => {
        // Автообновление каждые 5 секунд для отслеживания прогресса обработки
        // Но только если есть файлы в процессе обработки и нет ошибок
        if (hasError) return;

        const interval = setInterval(() => {
            const hasProcessingFiles = files.some(f => f.status === 'Processing');
            if (hasProcessingFiles) {
                loadFiles();
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [files, hasError]);

    useEffect(() => {
        // Проверяем изменение статуса с Processing на Completed
        files.forEach(file => {
            const prevStatus = previousStatuses[file.id];
            if (prevStatus === 'Processing' && file.status === 'Completed') {
                // Показываем тост без автозакрытия
                toast.info(
                    'Обработка списка массовой загрузки завершена. Ознакомиться с результатами и продолжить оформление можно в разделе "Массовые операции".',
                    {
                        autoClose: false,
                        closeButton: true,
                        closeOnClick: false
                    }
                );
            }
        });
        
        // Обновляем предыдущие статусы
        const newStatuses = {};
        files.forEach(file => {
            newStatuses[file.id] = file.status;
        });
        setPreviousStatuses(newStatuses);
    }, [files]);

    const loadFiles = () => {
        getMassUploadFiles()
            .then(data => {
                const newFiles = data.files || [];
                // Убрана проверка изменения статусов здесь - она уже выполняется в useEffect
                setFiles(newFiles);
                setLoading(false);
                setHasError(false);
            })
            .catch(err => {
                console.error('Error loading files:', err);
                setFiles([]);
                setLoading(false);
                setHasError(true);
                
                // Показываем ошибку только один раз
                if (!hasError) {
                    toast.error('Ошибка при загрузке списка файлов. Возможно, миграция БД не выполнена.');
                }
            });
    };

    const handleUploadComplete = (result) => {
        setShowUploadModal(false);
        
        // Файл загружен, обновляем список
        loadFiles();
        
        // Можно сразу перейти на страницу файла (он будет в статусе Processing)
        // history.push(`/massupload/edit/${result.fileId}`);
    };

    const handleDeleteFile = (fileId) => {
        if (confirm('Вы уверены, что хотите удалить этот файл?')) {
            deleteMassUploadFile(fileId)
                .then(() => {
                    toast.success('Файл удален');
                    loadFiles();
                })
                .catch(err => {
                    console.error(err);
                    toast.error('Ошибка при удалении файла');
                });
        }
    };

    // Фильтрация по ЮЛ (без автора, чтобы получить список доступных авторов)
    const filesFilteredByCompany = files.filter(file => {
        if (!companyFilter) {
            return true; // Если фильтр не выбран, показываем все файлы
        }
        // Проверяем, что companyId существует и соответствует фильтру
        if (!file.companyId) {
            return false; // Если у файла нет companyId, он не проходит фильтр
        }
        return file.companyId.toString() === companyFilter.toString();
    });

    // Извлекаем уникальных авторов из файлов, отфильтрованных по ЮЛ
    // Если выбрано ЮЛ, показываем только авторов, у которых есть файлы с этим ЮЛ
    const authorsMap = new Map();
    filesFilteredByCompany.forEach(file => {
        if (file.authorId && file.authorName && !authorsMap.has(file.authorId)) {
            authorsMap.set(file.authorId, {
                id: file.authorId,
                name: file.authorName
            });
        }
    });
    const authors = Array.from(authorsMap.values()).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
    );

    const availableCompanyIds = Array.from(
        new Set(
            files
                .filter(file => file.companyId)
                .map(file => file.companyId.toString())
        )
    );

    // Сбрасываем фильтр по автору, если выбранный автор не входит в список доступных авторов для выбранного ЮЛ
    useEffect(() => {
        if (companyFilter && authorFilter) {
            const authorExists = authors.some(author => author.id.toString() === authorFilter);
            if (!authorExists) {
                setAuthorFilter('');
            }
        }
    }, [companyFilter, authors, authorFilter]);

    // Фильтрация и пагинация
    const filteredFiles = files.filter(file => {
        const matchesSearch = file.originalFileName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = !statusFilter || file.status === statusFilter;
        const matchesAuthor = !authorFilter || (file.authorId && file.authorId.toString() === authorFilter.toString());
        
        // Фильтрация по компании
        let matchesCompany = true;
        if (companyFilter) {
            if (!file.companyId) {
                matchesCompany = false; // Если у файла нет companyId, он не проходит фильтр
            } else {
                matchesCompany = file.companyId.toString() === companyFilter.toString();
            }
        }
        
        return matchesSearch && matchesStatus && matchesAuthor && matchesCompany;
    });

    const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedFiles = filteredFiles.slice(startIndex, endIndex);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleString('ru-RU');
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="loading-spinner">Загрузка...</div>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="page-content mass-upload-page">
                <div className="page-header">
                    <h1>Массовые операции</h1>
                </div>
                <div className="alert alert-danger">
                    <h4><i className="fa fa-exclamation-triangle"></i> Ошибка загрузки данных</h4>
                    <p>Не удалось загрузить список файлов. Возможные причины:</p>
                    <ul>
                        <li>Миграция базы данных не выполнена (таблица MassUploadFiles не создана)</li>
                        <li>Проблемы с подключением к базе данных</li>
                        <li>Ошибка на сервере</li>
                    </ul>
                    <button className="btn btn-primary" onClick={() => {
                        setHasError(false);
                        setLoading(true);
                        loadFiles();
                    }}>
                        <i className="fa fa-refresh"></i> Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='new-styles new-styles__p-0'>
            <div className='grid__header'>
                <GridHeader
                    name="Массовые операции"
                    btn={
                        <Button
                            className='btn_light-blue btn_icon_plus'
                            onClick={() => setShowUploadModal(true)}
                        >
                            Загрузить новый файл
                        </Button>
                    }
                />
                <MassUploadFilters
                    statusFilter={statusFilter}
                    authorFilter={authorFilter}
                    companyFilter={companyFilter}
                    searchTerm={searchTerm}
                    onStatusChange={setStatusFilter}
                    onAuthorChange={setAuthorFilter}
                    onCompanyChange={setCompanyFilter}
                    onSearchChange={setSearchTerm}
                    authors={authors}
                    availableCompanyIds={availableCompanyIds}
                />
            </div>

            <PaginationControl
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredFiles.length}
                startIndex={startIndex}
                endIndex={endIndex}
                onPageChange={setCurrentPage}
            />

            <MassUploadTable
                files={paginatedFiles}
                onEdit={(fileId) => history.push(`/massupload/edit/${fileId}`)}
                onView={(file) => {
                    // Переходим на "Мои отправления" с фильтром по ID файла массовой загрузки
                    if (!file.parcelsCreatedDate) {
                        toast.warning('Накладные ещё не созданы для этого файла');
                        return;
                    }
                    
                    const filters = {
                        massUploadFileId: [file.id.toString()]
                    };
                    const filtersEncoded = encodeURIComponent(JSON.stringify(filters));
                    history.push(`/parcels/list/new?filters=${filtersEncoded}`);
                }}
                onDelete={handleDeleteFile}
                onDownload={downloadMassUploadSourceFile}
                formatDate={formatDate}
            />

            {showUploadModal && (
                <MassUploadModal
                    onClose={() => setShowUploadModal(false)}
                    onUploadComplete={handleUploadComplete}
                />
            )}
        </div>
    );
};

export default MassUploadListPage;




// WEBPACK FOOTER //
// ./App/pages/massUpload/massUploadListPage.jsx